import { Box, Text } from '@chakra-ui/react';
import { Tooltip } from '../../ui/Tooltip';
import type { ReactElement } from 'react';
import { NAV_RAIL_TARGET_SIZE } from './navRailWidth';

interface NavRailItemProps {
  label: string;
  /**
   * Hover hint. Only pass one that ADDS something the visible label doesn't —
   * a keyboard shortcut, or what the target actually is. A tooltip repeating
   * the label is noise, and it makes the accessible name announce twice.
   */
  tooltip?: string;
  icon: ReactElement;
  isActive?: boolean;
  /** True for stateful toggles (Chat) — swaps aria-current for aria-pressed. */
  isToggle?: boolean;
  /**
   * A toggle whose section is ALSO the current page: `aria-current="page"`
   * beside `aria-pressed` and the active look, while the toggle keeps its own
   * pressed state. No rail item needs it today.
   */
  isCurrentPage?: boolean;
  onClick: () => void;
  testId?: string;
}

/**
 * One target in the nav rail: icon over an 11px label. The rail's width is
 * `NAV_RAIL_WIDTH` in `navRailWidth.ts` — currently 88px, sized so this
 * component's 64px box keeps an even gutter either side.
 *
 * The rail never expands, so the label is always visible and IS the
 * accessible name — no tooltip, and deliberately no `aria-label`, which would
 * only be a second copy of the same string free to drift.
 *
 * Two aria modes, chosen by `isToggle`: route items (Home, Workspace,
 * Protocols) announce `aria-current="page"`; stateful toggles (Chat) announce
 * `aria-pressed`. A toggle must never claim `aria-current` for opening its
 * panel: that tells a screen-reader user they navigated when they only
 * opened a panel. It claims it only by route, through `isCurrentPage`.
 */
export function NavRailItem({
  label,
  tooltip,
  icon,
  isActive = false,
  isToggle = false,
  isCurrentPage = false,
  onClick,
  testId,
}: NavRailItemProps) {
  const current = isToggle ? isCurrentPage : isActive;
  const lit = isActive || isCurrentPage;
  const button = (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      aria-current={current ? 'page' : undefined}
      aria-pressed={isToggle ? isActive : undefined}
      data-testid={testId}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="2px"
      // This is the LABEL's box, and nothing else — since the 2026-09 design
      // the active fill lives on the chip below, so there is no pill for
      // the label to overflow. What it must do is hold the longest label in the
      // set ("Workspace", at 11px) on one line: `noOfLines={1}` ellipsises
      // rather than wraps, so a too-narrow item truncates a nav target's NAME
      // silently. Any new label has to fit, or the set needs a rethink.
      //
      // The ceiling is the rail: `NAV_RAIL_WIDTH` (88) minus the nav's px={1}
      // gutters is an 80px content box, so 64 sits centred with 8px either
      // side.
      // The rail's own width is chosen from the gutter this leaves, not
      // the other way round (see navRailWidth.ts): 84 gave 10px and read as
      // squashed against the content canvas, which is flush to the rail.
      // `NavRailItem.test.tsx` pins this width against the constant.
      w="64px"
      py={1}
      // The ITEM itself is transparent now. The 2026-09 design moves the
      // active/hover fill onto a chip behind the ICON only — a
      // full-item box on the new borderless rail read as a card floating on
      // the parchment frame, which is the thing the frame redesign removes.
      // The label sits outside the chip and carries the state in weight and
      // color instead.
      bg="transparent"
      color={lit ? 'text.primary' : 'text.secondary'}
      // Inset so the ring isn't clipped by the rail's bounds.
      _focusVisible={{
        outline: 'var(--chakra-borderWidths-interactive) solid',
        outlineColor: 'border.focus',
        outlineOffset: '-2px',
      }}
      // The chip is a child, so the item's own hover has to reach it.
      _hover={{ '& [data-nav-chip]': { bg: 'sidebar.item.hover' } }}
    >
      <Box
        data-nav-chip
        // Create's exact box (the NAV_RAIL_TARGET_SIZE square, `lg` radius), so
        // the lit item and the button above it are one shape.
        w={`${NAV_RAIL_TARGET_SIZE}px`}
        h={`${NAV_RAIL_TARGET_SIZE}px`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="lg"
        bg={lit ? 'sidebar.item.active' : 'transparent'}
        transition="background 0.15s"
      >
        {icon}
      </Box>
      <Text
        fontSize="11px"
        lineHeight="1.2"
        noOfLines={1}
        color="inherit"
        // `medium` and `semibold` both map to 600, so the active label was the
        // same weight as every inactive one and the state read off colour and
        // the pill background alone. `normal` puts the weight step back.
        fontWeight={lit ? 'semibold' : 'normal'}
      >
        {label}
      </Text>
    </Box>
  );
  if (!tooltip) return button;
  // `aria-label` is deliberately NOT set on the button: the visible label is the
  // accessible name. Chakra's Tooltip only adds aria-describedby, so the hint is
  // supplementary rather than replacing the name.
  return (
    <Tooltip label={tooltip} placement="right" hasArrow openDelay={400}>
      {button}
    </Tooltip>
  );
}
